import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import VerificationStep from './VerificationStep';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z" fill="#4285F4"/>
    <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
    <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
    <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
  </svg>
);

interface IniciarTrazadoSectionProps {
  openLoginModal?: () => void;
}

export default function IniciarTrazadoSection({ openLoginModal }: IniciarTrazadoSectionProps) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<'register' | 'login' | 'verify' | 'activated'>('register');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState({ name: '', email: '', motivation: '' });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleGoogleClick = async () => {
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (err) setError(err.message);
    setLoading(false);
  };

  const handleRegisterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const form = e.currentTarget;
    const data = new FormData(form);
    const name = data.get('nombre')?.toString() || '';
    const email = data.get('email')?.toString() || '';
    const motivation = data.get('motivacion')?.toString() || '';

    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        data: { full_name: name, motivation },
      },
    });

    if (err) {
      setError(err.message);
    } else {
      setUserData({ name, email, motivation });
      setMode('verify');
    }
    setLoading(false);
  };

  const handleLoginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const form = e.currentTarget;
    const data = new FormData(form);
    const email = data.get('email')?.toString() || '';

    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });

    if (err) {
      setError('No encontramos ese nodo. ¿Quieres iniciar un nuevo trazado?');
    } else {
      setUserData({ name: '', email, motivation: '' });
      setMode('verify');
    }
    setLoading(false);
  };

  const handleVerified = () => {
    setMode('activated');
    setTimeout(() => navigate('/dashboard'), 2000);
  };

  return (
    <section id="iniciar-trazado" ref={sectionRef} className="bg-[#F8F6F3] py-32 px-6 overflow-hidden">
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Copy */}
          <div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-[#8B9D83] text-xs uppercase tracking-[4px] mb-6">— 05 / Iniciar Trazado —</p>
            <h2 className="text-[#2A2D34] font-bold leading-tight mb-6" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', letterSpacing: '-1px' }}>
              Únete a la Red Geodésica.
            </h2>
            <p className="text-[#2A2D34]/60 text-lg leading-relaxed mb-10 max-w-md">
              Ocupa tu nodo. Comienza tu Re-ingeniería Sistémica hoy. El primer módulo es libre y sin compromiso.
            </p>
            <div className="space-y-4">
              {[
                { icon: 'ri-shield-check-line', text: 'Módulo 0 desbloqueado gratis al registrarte', color: '#8B9D83' },
                { icon: 'ri-lock-line', text: 'Verificación de nodo por correo electrónico', color: '#C97B63' },
                { icon: 'ri-star-line', text: 'App Estelarte gratuita para todos los miembros', color: '#8B9D83' },
                { icon: 'ri-community-line', text: 'Comunidad privada de la Red Geodésica', color: '#C97B63' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5" style={{ backgroundColor: `${item.color}20` }}>
                    <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
                  </div>
                  <p className="text-[#2A2D34]/70 text-sm leading-relaxed pt-1">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 rounded-2xl overflow-hidden w-full" style={{ height: '220px' }}>
              <img
                src="https://public.readdy.ai/ai/img_res/d29b529cddf0a214f10f5d448d375e34.jpg"
                alt="Red Geodésica"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right: Form Panel */}
          <div className={`transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-[#2A2D34] rounded-2xl p-10 relative overflow-hidden">
              <div
                className="absolute inset-0 pointer-events-none opacity-[0.03]"
                style={{
                  backgroundImage: 'linear-gradient(rgba(139,157,131,1) 1px, transparent 1px), linear-gradient(90deg, rgba(139,157,131,1) 1px, transparent 1px)',
                  backgroundSize: '28px 28px',
                }}
              />

              {/* Mode toggle */}
              {(mode === 'register' || mode === 'login') && (
                <div className="relative flex rounded-full mb-8 p-1" style={{ background: 'rgba(248,246,243,0.06)', border: '1px solid rgba(248,246,243,0.08)' }}>
                  <button
                    type="button"
                    onClick={() => { setMode('register'); setError(''); }}
                    className="flex-1 text-xs font-semibold py-2.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                    style={mode === 'register' ? { background: '#C97B63', color: '#F8F6F3' } : { color: 'rgba(248,246,243,0.4)' }}
                  >
                    Nuevo Trazado
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); }}
                    className="flex-1 text-xs font-semibold py-2.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                    style={mode === 'login' ? { background: '#8B9D83', color: '#1A1C20' } : { color: 'rgba(248,246,243,0.4)' }}
                  >
                    Ya tengo Nodo
                  </button>
                </div>
              )}

              <div className="relative">
                {/* REGISTER FORM */}
                {mode === 'register' && (
                  <>
                    <h3 className="text-[#F8F6F3] font-semibold text-xl mb-1">Introduce tus Coordenadas</h3>
                    <p className="text-[#F8F6F3]/40 text-sm mb-7">Accede al Módulo 0 de forma gratuita. Sin tarjeta de crédito.</p>

                    <button
                      type="button"
                      onClick={handleGoogleClick}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-3 bg-[#F8F6F3] hover:bg-white text-[#2A2D34] font-semibold text-sm py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap mb-5 disabled:opacity-60"
                    >
                      <GoogleIcon />
                      Registrarme con Google
                    </button>

                    <div className="flex items-center gap-3 mb-5">
                      <div className="flex-1 h-px bg-[#F8F6F3]/10" />
                      <span className="text-[#F8F6F3]/25 text-[10px] uppercase tracking-[2px]">o con email</span>
                      <div className="flex-1 h-px bg-[#F8F6F3]/10" />
                    </div>

                    <form onSubmit={handleRegisterSubmit} className="space-y-5">
                      <div>
                        <label className="block text-[#F8F6F3]/50 text-[10px] uppercase tracking-[2px] mb-2">Nombre Completo</label>
                        <input name="nombre" type="text" required placeholder="Tu nombre completo" className="w-full bg-[#353942] text-[#F8F6F3] text-sm px-4 py-3.5 rounded-lg border border-[#F8F6F3]/10 focus:outline-none focus:border-[#8B9D83] placeholder-[#F8F6F3]/25 transition-colors duration-200" />
                      </div>
                      <div>
                        <label className="block text-[#F8F6F3]/50 text-[10px] uppercase tracking-[2px] mb-2">La Clave de Alineación (Correo)</label>
                        <input name="email" type="email" required placeholder="tu@correo.com" className="w-full bg-[#353942] text-[#F8F6F3] text-sm px-4 py-3.5 rounded-lg border border-[#F8F6F3]/10 focus:outline-none focus:border-[#8B9D83] placeholder-[#F8F6F3]/25 transition-colors duration-200" />
                      </div>
                      <div>
                        <label className="block text-[#F8F6F3]/50 text-[10px] uppercase tracking-[2px] mb-2">¿Qué te trae a la Red Geodésica?</label>
                        <select name="motivacion" required defaultValue="" className="w-full bg-[#353942] text-[#F8F6F3] text-sm px-4 py-3.5 rounded-lg border border-[#F8F6F3]/10 focus:outline-none focus:border-[#8B9D83] transition-colors duration-200 cursor-pointer">
                          <option value="" disabled>Selecciona tu punto de entrada</option>
                          <option value="grounding">Grounding y Arraigo</option>
                          <option value="bio-santuario">Bio-Santuario y Entorno</option>
                          <option value="reprogramacion">Re-programación Mental</option>
                          <option value="elementos">Elementos y Cosmos</option>
                          <option value="soberania">Soberanía Personal</option>
                          <option value="constelaciones">Constelaciones Familiares</option>
                          <option value="todos">Quiero el Sistema Completo</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[#F8F6F3]/50 text-[10px] uppercase tracking-[2px] mb-2">Mensaje (opcional)</label>
                        <textarea name="mensaje" rows={3} maxLength={500} placeholder="Cuéntanos sobre tu Tensión Nodal actual..." className="w-full bg-[#353942] text-[#F8F6F3] text-sm px-4 py-3.5 rounded-lg border border-[#F8F6F3]/10 focus:outline-none focus:border-[#8B9D83] placeholder-[#F8F6F3]/25 transition-colors duration-200 resize-none" />
                      </div>
                      {error && <p className="text-[#C97B63] text-xs">{error}</p>}
                      <button type="submit" disabled={loading} className="w-full bg-[#C97B63] text-[#F8F6F3] font-semibold text-sm py-4 rounded-xl hover:bg-[#b86d55] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                        {loading ? <><i className="ri-loader-4-line animate-spin" /> Trazando tu Nodo...</> : <>Iniciar mi Trazado <i className="ri-arrow-right-line" /></>}
                      </button>
                      <p className="text-[#F8F6F3]/20 text-xs text-center leading-relaxed">Al registrarte aceptas nuestros términos. Tu información es sagrada y nunca será compartida.</p>
                    </form>
                  </>
                )}

                {/* LOGIN FORM */}
                {mode === 'login' && (
                  <>
                    <h3 className="text-[#F8F6F3] font-semibold text-xl mb-1">Reconexión Nodal</h3>
                    <p className="text-[#F8F6F3]/40 text-sm mb-7">Recibirás un código de acceso en tu correo.</p>

                    <button type="button" onClick={handleGoogleClick} disabled={loading} className="w-full flex items-center justify-center gap-3 bg-[#F8F6F3] hover:bg-white text-[#2A2D34] font-semibold text-sm py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap mb-5 disabled:opacity-60">
                      <GoogleIcon />
                      Continuar con Google
                    </button>

                    <div className="flex items-center gap-3 mb-5">
                      <div className="flex-1 h-px bg-[#F8F6F3]/10" />
                      <span className="text-[#F8F6F3]/25 text-[10px] uppercase tracking-[2px]">o con email</span>
                      <div className="flex-1 h-px bg-[#F8F6F3]/10" />
                    </div>

                    <form onSubmit={handleLoginSubmit} className="space-y-5">
                      <div>
                        <label className="block text-[#F8F6F3]/50 text-[10px] uppercase tracking-[2px] mb-2">Correo Electrónico</label>
                        <input name="email" type="email" required placeholder="tu@correo.com" className="w-full bg-[#353942] text-[#F8F6F3] text-sm px-4 py-3.5 rounded-lg border border-[#F8F6F3]/10 focus:outline-none focus:border-[#8B9D83] placeholder-[#F8F6F3]/25 transition-colors duration-200" />
                      </div>
                      <button type="button" onClick={openLoginModal} className="text-[#C97B63] text-xs hover:underline cursor-pointer">
                        ¿Olvidaste tu frecuencia de acceso?
                      </button>
                      {error && (
                        <div className="bg-[#C97B63]/10 border border-[#C97B63]/30 rounded-lg px-4 py-3">
                          <p className="text-[#C97B63] text-xs leading-relaxed">{error}</p>
                        </div>
                      )}
                      <button type="submit" disabled={loading} className="w-full bg-[#8B9D83] text-[#1A1C20] font-bold text-sm py-4 rounded-xl hover:bg-[#7c8f74] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                        {loading ? <><i className="ri-loader-4-line animate-spin" /> Verificando nodo...</> : <><i className="ri-login-box-line" /> Acceder al Control Center</>}
                      </button>
                    </form>
                  </>
                )}

                {/* VERIFY STEP */}
                {mode === 'verify' && (
                  <VerificationStep
                    userName={userData.name}
                    userEmail={userData.email}
                    onVerified={handleVerified}
                    onBack={() => setMode('register')}
                  />
                )}

                {/* ACTIVATED STATE */}
                {mode === 'activated' && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-6" style={{ border: '1px solid rgba(139,157,131,0.5)', background: 'rgba(139,157,131,0.12)' }}>
                      <i className="ri-checkbox-circle-line text-3xl text-[#8B9D83]" />
                    </div>
                    <h3 className="text-[#F8F6F3] font-bold text-xl mb-3">¡Nodo Activado! ⊹</h3>
                    <p className="text-[#F8F6F3]/60 text-sm leading-relaxed max-w-xs mx-auto mb-6">
                      Tu frecuencia ha sido validada. El <span className="text-[#8B9D83]">Nodo 0: El Origen</span> se ha desbloqueado para ti.
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-[#8B9D83] text-sm" />
                      <span className="text-[#F8F6F3]/40 text-xs">Redirigiendo al Control Center...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
